/**
 * ============================================================
 * ASSETRAC — Canonical MUI Theme Configuration
 * ============================================================
 *
 * Corrected against the Assetrac Design System (pencil-new.pen).
 * Iteration 9 — Design system correction release.
 *
 * ============================================================
 */

import { createTheme } from '@mui/material';

// ─────────────────────────────────────────────
// Brand Color Palette
// ─────────────────────────────────────────────
export const colors = {
  // Brand
  asseTracOrange:    '#EC4F36',   // Primary — buttons, active states, brand accent
  portlandCement:    '#E8DCDA',   // Secondary text, supporting content
  quillFeather:      '#FBFDFB',   // Primary text (near white)
  lightSteel:        '#666564',   // Dividers, borders, placeholders
  canary:            '#ECAE36',   // Warning / alerts

  // Surfaces (dark to light)
  background:        '#1E1D1C',   // Page background (corrected from #2D2C2B)
  backgroundMid:     '#2D2C2B',   // Section backgrounds
  surface:           '#3A3938',   // Cards, AppBar, Drawer
  surfaceContainer:  '#302F2E',   // Table headers, slightly elevated containers
  surfaceBright:     '#4A4948',   // Hover states on surfaces

  // Semantic
  success:           '#4CAF50',
  successBg:         '#163316',
  error:             '#CF3028',   // Corrected (was #f44336 from MUI default)
  errorBg:           '#410002',
  info:              '#316EC0',
  infoBg:            '#14203A',
  midnightBlue:      '#0A1034',

  // Interaction states (R-04 ASS-8, 2026-09-09)
  // Named for the STATE, not the component: reusable across every hover/
  // selected surface in the app. Values copied exactly from prior rgba
  // literals; the point of this mint is centralisation, not recolouring.
  //
  // FOLLOW-UP: the MUI-correct home for these is palette.action.hover /
  // action.selected / action.selectedOpacity etc. That would drive every
  // MUI component's hover, not just the nav — right architecture, wrong
  // week. Filed for post-demo revisit.
  stateTextDim:       'rgba(255, 255, 255, 0.7)',   // Dimmed inactive text over surface
  stateHover:         'rgba(232, 220, 218, 0.05)',  // Subtle hover tint
  stateSelected:      'rgba(255, 255, 255, 0.10)',  // Selected-item background
  stateSelectedHover: 'rgba(255, 255, 255, 0.13)',  // Selected-item hover
};

// Backward-compatible alias — existing components import `colorPalette`
export const colorPalette = {
  asseTracOrange:    colors.asseTracOrange,
  portlandCement:    colors.portlandCement,
  quillFeather:      colors.quillFeather,
  lightSteel:        colors.lightSteel,
  backgroundColor:   colors.surface,          // #3A3938 (paper/cards)
  backgroundDarker:  colors.background,       // #1E1D1C (corrected from #2D2C2B)
  canary:            colors.canary,
  midnightBlue:      colors.midnightBlue,
  success:           colors.success,
  error:             colors.error,            // #CF3028 (corrected from #f44336)
};

// ─────────────────────────────────────────────
// Spacing Constants (multiples of 4)
// ─────────────────────────────────────────────
export const SPACING = {
  none: 0,  // 0px
  xxs: 4,   // 4px
  xs: 8,    // 8px
  sm: 12,   // 12px
  md: 16,   // 16px
  lg: 20,   // 20px
  xl: 24,   // 24px
  xxl: 28,  // 28px
  xxxl: 32, // 32px
};

// ─────────────────────────────────────────────
// Filter Thresholds
// ─────────────────────────────────────────────
export const FILTER_THRESHOLDS = {
  BATTERY_ISSUE_PERCENT: 20,  // Battery percentage below which asset is flagged
};

// ─────────────────────────────────────────────
// Shadow Constants
// ─────────────────────────────────────────────
export const SHADOWS = {
  popover: '0px 14px 21px rgba(0, 0, 0, 0.25)',  // Popover/Menu drop shadow: #000000 25% opacity, 21px blur, 14px Y offset
  hexagonMarker: '0px 4px 8px rgba(0, 0, 0, 0.3)', // Drop shadow for hexagon project markers
};

// ─────────────────────────────────────────────
// Map Layer Constants
// ─────────────────────────────────────────────
export const MAP_LAYERS = {
  projectBoundary: {
    fillOpacity: 0.2,           // 20% opacity for boundary fill
    fillColor: [255, 255, 255, 51],  // White with 20% opacity (255 * 0.2 = 51)
    strokeColor: [255, 255, 255],    // White border
    strokeWidth: 3,
  },
  hexagonMarker: {
    radius: 20,                      // Hexagon radius in pixels
    fillColor: [236, 79, 54, 255],  // Orange (#EC4F36)
    strokeColor: [255, 255, 255, 255], // White border
    strokeWidth: 2,                  // 2px border
    shadowColor: [0, 0, 0, 80],     // Semi-transparent black shadow
    shadowOffset: [0.0001, -0.0001], // Slight offset for shadow
    textColor: [255, 255, 255, 255], // White text for P/Y labels
    textSize: 18,                    // Font size for labels
  },
};

// ─────────────────────────────────────────────
// Project/Yard Selector Constants
// ─────────────────────────────────────────────
export const PROJECT_SELECTOR = {
  fontSize: 18,                   // Text size 18px as specified
  fontWeight: 700,                // Bold weight
  fontStyle: 'normal',            // Not italics
  borderStyle: 'none',            // No border as specified
  minWidth: 200,                  // Minimum width
};

// ─────────────────────────────────────────────
// Page Body Layout Constants
// ─────────────────────────────────────────────
export const PAGE_BODY = {
  maxWidth: {
    mobile: '94vw',       // Mobile breakpoint
    desktop: '88vw',      // Desktop and tablet breakpoints
  },
  padding: {
    top: 40,              // 40px
    horizontal: 24,       // 24px (left and right)
    bottom: 8,            // 8px
  },
  margins: {
    titleBottom: 24,      // 24px margin bottom for page titles
    filterBottom: 16,     // 16px margin bottom for search/filter areas
  },
};

// ─────────────────────────────────────────────
// Canonical Theme
// ─────────────────────────────────────────────
export const theme = createTheme({

  // ── Spacing (base unit = 4px for multiples of 4 convention) ────
  spacing: 4,

  // ── Palette ─────────────────────────────────
  palette: {
    mode: 'dark',

    primary: {
      main:         colors.asseTracOrange,   // #EC4F36
      dark:         '#D94530',               // hover state
      contrastText: '#FFFFFF',
    },
    secondary: {
      main:         colors.quillFeather,     // #FBFDFB
      contrastText: colors.background,
    },
    error: {
      main:         colors.error,            // #CF3028 (corrected — was canary yellow!)
      contrastText: '#FFFFFF',
    },
    warning: {
      main:         colors.canary,           // #ECAE36
      contrastText: '#000000',
    },
    success: {
      main:         colors.success,          // #4CAF50
      contrastText: '#FFFFFF',
    },
    info: {
      main:         colors.info,             // #316EC0
      contrastText: '#FFFFFF',
    },

    background: {
      default:      colors.surface,          // #3A3938 — page bg (same as navbar)
      paper:        colors.surface,          // #3A3938 — cards, drawer, appbar
    },

    text: {
      primary:      '#E8DCDA',               // $on-surface (was #FBFDFB)
      secondary:    '#A09C9A',               // $on-surface-variant (was #E8DCDA)
      disabled:     colors.lightSteel,       // #666564
    },

    divider:        colors.lightSteel,       // #666564

    // ── Semantic aliases (Iteration 12) ──────────────────────────────
    // These map to existing palette entries — no new colors.
    // supply counts use info tone; demand counts use warning tone.
    supply: {
      main: '#316EC0',       // same as info.main
    },
    demand: {
      main: '#ECAE36',       // same as warning.main
    },
    neutral: {
      // R-46 L-2: specify `main` only; MUI computes light/dark from it
      // via tonalOffset. Removed hand-derived light/dark; contrastText
      // stays because it's not derivable.
      main: '#9E9E9E',       // grey[500]
      contrastText: '#000000',
    },
  },

  // ── Typography ───────────────────────────────
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),

    h1: { fontWeight: 600 },
    h2: { fontWeight: 600 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 500 },
    h5: { fontWeight: 500 },
    h6: { fontWeight: 500 },

    subtitle1: {
      fontSize: '1.125rem',    // 18px (rem equivalent of 18px)
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '1.125rem',    // 18px (rem equivalent of 18px)
      fontWeight: 600,
      lineHeight: 1.5,
    },

    button: {
      textTransform: 'none',
      fontWeight: 500,
    },

    overline: {
      fontSize: '0.625rem',    // 10px
      fontWeight: 600,
      letterSpacing: '2px',
    },
  },

  // ── Shape ────────────────────────────────────
  shape: {
    borderRadius: 8,           // Base unit — components scale from this
  },

  // ── Component Overrides ──────────────────────
  components: {

    // Global CSS baseline
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `${colors.lightSteel} ${colors.background}`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: colors.lightSteel,
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: colors.background,
          },
        },
        // R-01 (ASS-5): mask the browser's off-palette autofill blue.
        // Chrome/Safari paint `-webkit-autofill` inputs with a hard-coded
        // blue that ignores background-color; the only reliable override is
        // a long inset box-shadow painted in the theme's own field-surface
        // colour, plus an explicit -webkit-text-fill-color for the text.
        // Covers hover/focus/active variants so the mask survives interaction.
        // Applied globally at the CssBaseline layer so every autofilled
        // input on every surface inherits the fix, not just the login form.
        'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active': {
          WebkitBoxShadow: `0 0 0 1000px ${colors.surface} inset`,
          boxShadow: `0 0 0 1000px ${colors.surface} inset`,
          WebkitTextFillColor: colors.quillFeather,
          caretColor: colors.quillFeather,
          transition: 'background-color 5000s ease-in-out 0s',
        },
        'textarea:-webkit-autofill, textarea:-webkit-autofill:hover, textarea:-webkit-autofill:focus, textarea:-webkit-autofill:active': {
          WebkitBoxShadow: `0 0 0 1000px ${colors.surface} inset`,
          boxShadow: `0 0 0 1000px ${colors.surface} inset`,
          WebkitTextFillColor: colors.quillFeather,
          caretColor: colors.quillFeather,
          transition: 'background-color 5000s ease-in-out 0s',
        },
      },
    },

    // ── AppBar (TopBar) ────────────────────────
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        position: 'static',
        color: 'default',
      },
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,           // #3A3938
          borderBottom: `1px solid ${colors.lightSteel}`,
          boxShadow: 'none',
          color: colors.quillFeather,
        },
      },
    },

    // Toolbar height
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '52px !important',
          paddingLeft: '24px !important',
          paddingRight: '24px !important',
          gap: 8,
          '& > *': {
            marginBottom: '0 !important',
          },
          // Mobile: use smaller padding
          '@media (max-width: 600px)': {
            paddingLeft: '8px !important',
            paddingRight: '8px !important',
          },
        },
      },
    },

    // ── Drawer (SideNav) ──────────────────────
    MuiDrawer: {
      defaultProps: {
        anchor: 'left',
        variant: 'temporary',
      },
      styleOverrides: {
        paper: {
          width: 280,
          backgroundColor: colors.surface,           // #3A3938
          borderRight: `1px solid ${colors.lightSteel}`,
          color: colors.quillFeather,
        },
      },
    },

    // ── Buttons ───────────────────────────────
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: false,
      },
      styleOverrides: {
        root: {
          borderRadius: 28,                           // Rounded (not pill)
          padding: '8px 24px',
          fontSize: 14,
          fontWeight: 500,
          textTransform: 'none',
          boxShadow: 'none',
          height: 44,                                 // Standard button height
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          '&:hover': {
            backgroundColor: '#D94530',
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: '#EC4F36',
          color: '#FFFFFF',
          '&:hover': { backgroundColor: '#D94530' },
        },
        outlined: {
          color: '#FFFFFF',
          borderColor: 'rgba(255, 255, 255, 0.60)',
          '&:hover': {
            borderColor: '#FFFFFF',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.38)',
            borderColor: 'rgba(102, 101, 100, 0.5)',
          },
        },
        text: {
          color: colors.portlandCement,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
        sizeSmall: {
          padding: '5px 14px',
          fontSize: 12,
          height: 36,                                 // Small button height
        },
      },
    },

    // ── Icon Buttons ──────────────────────────
    MuiIconButton: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',            // Corrected (was #525150)
          color: '#E8DCDA',                          // $on-surface (was #EFEDEC)
          borderRadius: '8px',                       // Rounded square (was 50%)
          width: 40,
          height: 40,
          '&:hover': {
            backgroundColor: 'rgba(232, 220, 218, 0.08)',  // Corrected (was #5D5C5B)
          },
          '&.MuiIconButton-sizeSmall': {
            width: 32,
            height: 32,
          },
        },
      },
    },

    // ── Text Fields ───────────────────────────
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        autoComplete: 'off',
        size: 'medium',
      },
      styleOverrides: {
        root: {
          marginBottom: '16px',                       // Global bottom margin for text fields
          '& .MuiOutlinedInput-root': {
            height: 40,                               // Standard text field height
            '& fieldset': {
              borderColor: colors.lightSteel,         // #666564 default
            },
            '&:hover fieldset': {
              borderColor: colors.portlandCement,     // #E8DCDA hover
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.asseTracOrange,     // #EC4F36 focus
            },
            // Multiline text fields (like Description)
            '&.MuiInputBase-multiline': {
              height: 'auto',
              minHeight: 120,                         // Min-height for long-form text fields
            },
          },
          '& .MuiInputBase-input': {
            fontSize: 14,
            '&[type="date"]': {
              marginBottom: 0,                        // Remove margin-bottom for date inputs
            },
          },
          // Remove margin-bottom for date input fields specifically
          '&:has(input[type="date"])': {
            marginBottom: 0,
          },
        },
      },
    },
    
    // ── Input Labels ──────────────────────────
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 14,
          color: colors.lightSteel,
          transform: 'translate(14px, 10px) scale(1)', // Adjusted for -3px top margin (13px - 3px = 10px)
          '&.Mui-focused': {
            color: colors.asseTracOrange,
          },
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)', // Standard shrink position
          },
        },
      },
    },
    
    // ── OutlinedInput ─────────────────────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          height: 40,                                 // Standard input height
        },
      },
    },

    // ── Select ────────────────────────────────
    MuiSelect: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          height: 40,                                 // Standard select height to match inputs
          fontSize: 14,                               // Consistent font size
        },
        select: {
          fontSize: 14,                               // Ensure select text is consistent
        },
        icon: { 
          color: '#E8DCDA',
        },
      },
    },

    // ── Form Control ──────────────────────────
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: '16px',                       // Consistent spacing with TextFields
          '& .MuiOutlinedInput-root': {
            height: 40,                               // Ensure consistent height
          },
        },
      },
    },

    // ── Chips ─────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 9999,                        // Pill shape (corrected from inheriting 8)
          fontSize: 12,
          fontWeight: 500,
        },
        sizeSmall: {
          height: 22,
          fontSize: 11,
        },
      },
    },

    // ── Lists (NavItems) ──────────────────────
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          color: 'rgba(255, 255, 255, 0.70)',
          transition: 'background-color 150ms ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.10)',
            color: colors.quillFeather,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.13)',
            },
          },
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: 13,
          fontWeight: 400,
          letterSpacing: '1px',
          textTransform: 'uppercase',
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
          color: 'inherit',
        },
      },
    },

    // ── Tables ────────────────────────────────
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,           // #3A3938
          borderRadius: 8,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surfaceContainer,  // #302F2E
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #4A4847',
          fontSize: 14,
          padding: '0 16px',
          color: '#E8DCDA',
        },
        head: {
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '1px',
          textTransform: 'uppercase',
          color: '#A09C9A',
          height: 44,
          backgroundColor: colors.surfaceContainer,
        },
        body: {
          height: 56,
          color: colors.portlandCement,
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          cursor: 'pointer',
          transition: 'background-color 100ms ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
          },
          '&:last-child td': {
            borderBottom: 'none',
          },
        },
      },
    },

    MuiTablePagination: {
      styleOverrides: {
        root: { color: '#A09C9A', borderTopColor: '#4A4847' },
        selectLabel: { color: '#A09C9A' },
        displayedRows: { color: '#A09C9A' },
        select: { color: '#E8DCDA' },
        selectIcon: { color: '#E8DCDA' },
        actions: { color: '#E8DCDA' },
      },
    },

    // ── Tabs ──────────────────────────────────
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 500,
          letterSpacing: '0',
          textTransform: 'none',
          color: '#E8DCDA',                            // text-primary (#E8DCDA)
          minHeight: 52,                               // Tabs fill the 52px bar
          padding: '8px 16px',
          '&.Mui-selected': {
            color: colors.asseTracOrange,
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 52,
        },
        indicator: {
          backgroundColor: colors.asseTracOrange,
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },

    // ── Paper / Cards ─────────────────────────
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',          // Remove MUI gradient overlay on dark mode
          boxShadow: 'none',                // Global: no drop shadows on cards/tables
        },
        elevation1: {
          boxShadow: 'none',
        },
        elevation4: {
          boxShadow: 'none',
        },
      },
    },
    
    // ── Card ──────────────────────────────────
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,
          border: `1px solid ${colors.lightSteel}`,
          boxShadow: 'none',                // No drop shadows on cards
        },
      },
    },

    // ── Divider ───────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.lightSteel,   // #666564
        },
        vertical: {
          minHeight: 36,                    // Changed from max-height to min-height
        },
        flexItem: {
          minHeight: 36,                    // Changed from max-height to min-height
        },
      },
    },

    // ── Checkbox ──────────────────────────────
    MuiCheckbox: {
      styleOverrides: {
        root: {
          width: 24,
          height: 24,
          padding: 0,
          borderRadius: 4,
          color: colors.lightSteel,
          '&.Mui-checked': {
            color: colors.asseTracOrange,
          },
          '&.MuiCheckbox-indeterminate': {
            color: colors.asseTracOrange,
          },
          '& .MuiSvgIcon-root': {
            width: 24,
            height: 24,
          },
        },
      },
    },

    // ── Tooltip ───────────────────────────────
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        placement: 'top',
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.surfaceBright,
          fontSize: 12,
        },
        arrow: {
          color: colors.surfaceBright,
        },
      },
    },

    // ── Dialog ────────────────────────────────
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.surface,
          border: `1px solid ${colors.lightSteel}`,
          borderRadius: 16,
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: 16,
          fontWeight: 600,
          padding: '20px 24px 12px',
        },
      },
    },

    // ── Snackbar / Alert ──────────────────────
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      },
    },

    // ── Input Adornments ──────────────────────
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: colors.lightSteel,
          '& .MuiIconButton-root': {
            color: '#E8DCDA !important',
            backgroundColor: 'transparent !important',
            borderRadius: '8px',
            padding: '8px',
            width: 'auto',
            height: 'auto',
            '&:hover': {
              backgroundColor: 'rgba(232, 220, 218, 0.08) !important',
            },
          },
        },
      },
    },

    // ── Menu ──────────────────────────────────
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.surface,
          border: `1px solid ${colors.lightSteel}`,
          borderRadius: 8,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 14,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(236, 79, 54, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(236, 79, 54, 0.18)',
            },
          },
        },
      },
    },

    // ── Popover ───────────────────────────────
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.surfaceContainer,   // #302F2E - Updated per design spec
          border: 'none',                              // No border per design spec
          borderRadius: 8,
          boxShadow: SHADOWS.popover,                 // Design spec shadow: #000000 25% opacity, 21px blur, 14px Y offset
        },
      },
    },

    // ── Autocomplete ──────────────────────────
    MuiAutocomplete: {
      styleOverrides: {
        popupIndicator: { color: '#E8DCDA' },
        clearIndicator: { color: '#E8DCDA' },
        paper: {
          backgroundColor: colors.surface,
          color: '#E8DCDA',
        },
        option: {
          color: '#E8DCDA',
          '&:hover': { backgroundColor: 'rgba(232, 220, 218, 0.08)' },
          '&[aria-selected="true"]': { backgroundColor: 'rgba(236, 79, 54, 0.16)' },
        },
      },
    },
    
    // ── DatePicker Styling ──────────────────────
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: '#E8DCDA',
          '&:hover': {
            backgroundColor: 'rgba(236, 79, 54, 0.16)',
          },
          '&.Mui-selected': {
            backgroundColor: colors.asseTracOrange,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#D94530',
            },
          },
          '&.MuiPickersDay-today': {
            borderColor: colors.asseTracOrange,
          },
        },
      },
    },

    MuiDateCalendar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,
          color: '#E8DCDA',
        },
      },
    },

    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          color: '#E8DCDA',
        },
        switchViewButton: {
          color: '#E8DCDA',
        },
        switchViewIcon: {
          color: '#E8DCDA',
        },
      },
    },

    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          color: '#A09C9A',
        },
      },
    },

    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: {
          color: '#E8DCDA',
        },
      },
    },

    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.surface,
          border: `1px solid ${colors.lightSteel}`,
          boxShadow: 'none',
        },
      },
    },

    MuiPickersLayout: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,
        },
      },
    },

    MuiYearCalendar: {
      styleOverrides: {
        root: {
          color: '#E8DCDA',
        },
      },
    },

    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          color: '#E8DCDA',
          '&:hover': {
            backgroundColor: 'rgba(236, 79, 54, 0.16)',
          },
          '&.Mui-selected': {
            backgroundColor: colors.asseTracOrange,
            color: '#FFFFFF',
          },
        },
      },
    },

    MuiMonthCalendar: {
      styleOverrides: {
        root: {
          color: '#E8DCDA',
        },
      },
    },

    MuiPickersMonth: {
      styleOverrides: {
        monthButton: {
          color: '#E8DCDA',
          '&:hover': {
            backgroundColor: 'rgba(236, 79, 54, 0.16)',
          },
          '&.Mui-selected': {
            backgroundColor: colors.asseTracOrange,
            color: '#FFFFFF',
          },
        },
      },
    },
  },

  // Custom theme properties (accessible via theme.custom)
  custom: {
    colors: colors,
    textfield: {
      '& .MuiInputBase-root': {
        color: colors.quillFeather,
      },
      '& .MuiInputLabel-root': {
        color: colors.portlandCement,
      },
    },
  },
});

// ─────────────────────────────────────────────
// Component dimension constants
// ─────────────────────────────────────────────
export const LAYOUT = {
  TOPBAR_HEIGHT:      52,    // px
  DRAWER_WIDTH:       280,   // px — temporary sidebar
  ASSET_PANEL_WIDTH:  400,   // px — asset detail side panel on map
  SEARCH_WIDTH:       280,   // px — global topbar search
  UTILITY_BAR_HEIGHT: 52,    // px — secondary toolbar on map pages
};

// ─────────────────────────────────────────────
// Status → MUI color mapping helpers
// ─────────────────────────────────────────────

/**
 * Returns MUI Chip color prop for a given asset status.
 * Use with: <Chip label={status} color={getAssetStatusColor(status)} />
 */
export function getAssetStatusColor(status) {
  switch (status?.toLowerCase()) {
    case 'active':      return 'success';
    case 'inactive':    return 'default';
    case 'pending':     return 'warning';
    case 'maintenance': return 'warning';
    case 'retired':     return 'default';
    default:            return 'default';
  }
}

/**
 * Returns MUI Chip color prop for a given project status.
 */
export function getProjectStatusColor(status) {
  switch (status?.toLowerCase()) {
    case 'active':    return 'success';
    case 'planned':   return 'warning';
    case 'complete':  return 'info';
    case 'inactive':  return 'default';
    default:          return 'default';
  }
}

/**
 * Returns CSS class name for custom status chip styling.
 * Use when MUI semantic colors don't match design exactly.
 */
export function getStatusChipClass(status) {
  const s = status?.toLowerCase();
  const map = {
    active:      'status-chip--active',
    inactive:    'status-chip--inactive',
    pending:     'status-chip--pending',
    dirty:       'status-chip--dirty',
    retired:     'status-chip--retired',
    planned:     'status-chip--proj-planned',
    complete:    'status-chip--proj-complete',
  };
  return map[s] || 'status-chip--inactive';
}

export default theme;
